import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { ComponentProps } from "react";
import { PageHeaderSkeleton, TableCardSkeleton } from "@/components/loading";
import { DashboardPageHeader, DashboardPageShell } from "@/components/patterns/headers";
import { PromptsTable } from "@/components/prompts-table/prompts-table";
import { trpcClient, useTRPC } from "@/lib/trpc";

type PromptsCallbackParams = Parameters<NonNullable<ComponentProps<typeof PromptsTable>["callbackFn"]>>[0];

export const Route = createFileRoute("/_dashboard/$brand/prompts")({
  component: RouteComponent,
});

function RouteComponent() {
  const { brand } = Route.useParams();
  const trpc = useTRPC();

  // Fetch brand by slug from URL params - this works on page reload
  const { data: brandObject, isLoading: isLoadingBrand } = useQuery(
    trpc.brands.getBrandBySlug.queryOptions({ slug: brand }),
  );

  // Show loading while fetching brand
  if (isLoadingBrand || !brandObject?.id) {
    return (
      <DashboardPageShell as="div" contentClassName="space-y-6">
        <PageHeaderSkeleton compact />
        <TableCardSkeleton rows={6} columns={5} showToolbar showFooter />
      </DashboardPageShell>
    );
  }

  const promptsCallback = ({
    skip,
    take,
    sortBy,
    sortOrder,
    textFilter,
  }: PromptsCallbackParams) =>
    trpcClient.prompts.getBrandPrompts.query({
      brandId: brandObject.id,
      skip,
      take,
      sortBy,
      sortOrder,
      textFilter,
    });

  return (
    <DashboardPageShell as="div" contentClassName="space-y-6">
      <DashboardPageHeader title="Prompts" />
      <PromptsTable callbackFn={promptsCallback} />
    </DashboardPageShell>
  );
}
